import * as THREE from 'three'
import type { IGSplatNode } from '@/plugins/gsplat/types/LODTypes'
import type {
  IGSplatGeometry,
  IGSplatMaterial,
  IGSplatResource,
  ISogsData,
  ISplatData,
} from '@/plugins/gsplat/types/ResourceTypes'
import { getLoggerManager } from '@/shared/logging'
import type { GSplatPlacement, Interval } from './GSplatPlacement'

const log = getLoggerManager().getLogger('gsplat:lod')

/**
 * 类型守卫：检查 gsplatData 是否为 ISplatData（PLY 格式）
 */
function isSplatData(data: unknown): data is ISplatData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'getProp' in data &&
    typeof (data as ISplatData).getProp === 'function'
  )
}

/**
 * 类型守卫：检查 gsplatData 是否为 ISogsData（SOGS CPU 格式）
 */
function isSogsData(data: unknown): data is ISogsData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'getSplatCenter' in data &&
    typeof (data as ISogsData).getSplatCenter === 'function'
  )
}

/**
 * Splat 状态枚举（用于中心点渲染模式的选中/锁定/删除状态）
 * 与 SuperSplat 的状态位定义一致
 */
export enum SplatState {
  /** 无状态 */
  NONE = 0,
  /** 选中状态 (bit 0) */
  SELECTED = 1,
  /** 锁定状态 (bit 1) */
  LOCKED = 2,
  /** 删除状态 (bit 2) */
  DELETED = 4,
}

/**
 * 临时数组池（用于区间排序和合并）
 * 对应 PlayCanvas 的全局 vecs 数组
 */
const tempIntervals: Interval[] = []

/**
 * GSplat 元信息和运行时状态
 *
 * 核心功能：
 * 1. Transform 变化检测（通过矩阵比较）
 * 2. 颜色累积器管理（用于 SH 颜色更新优化）
 * 3. 区间合并和管理（用于部分可见性）
 * 4. 视口和分块信息存储
 *
 * @remarks
 * 对应 PlayCanvas 的 GSplatInfo 类
 * 主要差异：GraphNode → Object3D，Mat4 → Matrix4，Vec4 → Vector4
 */
export class GSplatInfo {
  /**
   * GSplat 资源引用（支持 GSplatResource 和 GSplatSogsResource）
   */
  readonly resource: IGSplatResource

  /**
   * 关联的 GaussianSplatGeometry（可选）
   * 用于访问 GPU 纹理（colorTexture、dataTextureA/B、shTextures 等）
   */
  geometry?: IGSplatGeometry

  /**
   * 关联的材质（可选）
   */
  material?: IGSplatMaterial

  /**
   * 场景节点引用
   */
  readonly node: IGSplatNode

  /** PlayCanvas 风格 placement 引用，用于 WorkBuffer modifier / update mode。 */
  readonly placement: GSplatPlacement

  /**
   * LOD 级别
   */
  readonly lodIndex: number

  /**
   * 总 Splat 数量
   */
  readonly numSplats: number

  /**
   * 激活的 Splat 数量（受区间限制后的实际数量）
   */
  activeSplats: number = 0

  /**
   * 扁平化的区间数组
   * 每两个连续数字表示一个半开区间 [start, end)
   *
   * @example
   * [0, 100, 200, 300] 表示两个区间：[0, 100) 和 [200, 300)
   */
  intervals: number[] = []

  /**
   * 排序纹理的起始行
   */
  lineStart: number = 0

  /**
   * 排序纹理的行数
   */
  lineCount: number = 0

  /**
   * 填充的 Splat 数量（用于对齐纹理大小）
   */
  padding: number = 0

  /**
   * 视口信息（x, y, width, height）
   */
  viewport: THREE.Vector4 = new THREE.Vector4()

  /**
   * 上一帧的世界变换矩阵（用于检测 Transform 变化）
   */
  private lastMatrix: THREE.Matrix4 = new THREE.Matrix4()

  /**
   * 本地空间 AABB
   */
  aabb: THREE.Box3 = new THREE.Box3()

  /**
   * 世界空间 AABB（缓存）
   * 当 Transform 改变时需要重新计算
   */
  private worldAabb: THREE.Box3 = new THREE.Box3()

  /**
   * 累积的旋转角度（用于 SH 颜色更新触发）
   * 当累积值超过阈值时，触发颜色更新
   */
  colorAccumulatedRotation: number = 0

  /**
   * 累积的平移距离（用于 SH 颜色更新触发）
   * 当累积值超过阈值时，触发颜色更新
   */
  colorAccumulatedTranslation: number = 0

  /**
   * LOD 区间重映射纹理
   * 将目标索引映射到原始 splat 索引
   * 当 intervals.length > 0 时创建
   */
  intervalTexture: THREE.DataTexture | null = null

  // ============================================================
  // 状态管理（用于中心点渲染模式）
  // ============================================================

  /**
   * Splat 状态数据数组（每个元素对应一个 splat）
   * 使用位标志存储状态：SELECTED=1, LOCKED=2, DELETED=4
   */
  private _stateData: Uint8Array | null = null

  /**
   * 状态纹理（R8 格式，用于 GPU 读取）
   */
  private _stateTexture: THREE.DataTexture | null = null

  /**
   * 状态是否已修改（需要更新纹理）
   */
  private _stateDirty: boolean = false

  /**
   * 创建新的 GSplatInfo 实例
   *
   * @param resource - GSplat 资源
   * @param placement - 放置信息
   * @param geometry - 关联的 GaussianSplatGeometry（可选）
   */
  constructor(resource: IGSplatResource, placement: GSplatPlacement, geometry?: IGSplatGeometry) {
    if (!resource) {
      throw new Error('GSplatInfo: resource is required')
    }
    if (!placement) {
      throw new Error('GSplatInfo: placement is required')
    }

    this.resource = resource
    this.placement = placement
    this.node = placement.node
    this.lodIndex = placement.lodIndex
    this.numSplats = resource.numSplats || 0
    this.aabb.copy(placement.aabb)
    if (geometry !== undefined) {
      this.geometry = geometry
    }

    // 初始化区间
    this.updateIntervals(placement.intervals)

    // 初始化世界空间 AABB
    this.node.updateMatrixWorld(true)
    this.lastMatrix.copy(this.node.matrixWorld)
    this.updateWorldAabb()
  }

  /**
   * 销毁资源
   */
  destroy(): void {
    this.intervals.length = 0

    // 销毁 intervalTexture
    if (this.intervalTexture) {
      this.intervalTexture.dispose()
      this.intervalTexture = null
    }

    // 销毁状态纹理
    if (this._stateTexture) {
      this._stateTexture.dispose()
      this._stateTexture = null
    }
    this._stateData = null
    this._stateDirty = false
  }

  /**
   * 设置排序纹理的行信息
   *
   * @param start - 起始行
   * @param count - 行数
   * @param textureSize - 纹理宽度（每行的 splat 数量）
   * @param activeSplats - 激活的 splat 数量
   */
  setLines(start: number, count: number, textureSize: number, activeSplats: number): void {
    this.lineStart = start
    this.lineCount = count
    this.padding = textureSize * count - activeSplats

    if (this.padding < 0) {
      log.warn(`GSplatInfo.setLines: padding is negative ${this.padding}`)
    }

    this.viewport.set(0, start, textureSize, count)

    if (textureSize === 0 || count === 0) {
      log.warn(
        `[GSplatInfo] viewport is zero texSize=${textureSize} count=${count} start=${start} active=${activeSplats}`
      )
    }
  }

  /**
   * 从 Placement 的区间映射表更新扁平化区间数组
   *
   * @param intervals - 节点索引 → 闭区间 [start, end] 的映射
   *
   * @remarks
   * 算法步骤：
   * 1. 检查区间是否覆盖完整范围（快速路径）
   * 2. 对区间按起始位置排序
   * 3. 合并相邻区间
   * 4. 输出半开区间数组 [start1, end1, start2, end2, ...]
   */
  updateIntervals(intervals: Map<number, Interval>): void {
    this.intervals.length = 0
    this.activeSplats = this.numSplats

    // 如果 placement 定义了区间
    if (intervals.size > 0) {
      // 快速路径：检查区间是否覆盖完整范围
      // 同时收集区间到临时数组中用于排序
      let totalCount = 0
      let used = 0

      intervals.forEach((interval) => {
        totalCount += interval.end - interval.start + 1 // 闭区间长度
        tempIntervals[used++] = interval
      })

      // 如果不是完整范围，需要合并区间
      if (totalCount !== this.numSplats) {
        // 调整临时数组长度
        tempIntervals.length = used

        // 按起始位置排序
        tempIntervals.sort((a, b) => a.start - b.start)

        // 预分配上界大小（每个区间最多产生 2 个数字）
        this.intervals.length = used * 2

        // 合并相邻区间并直接写入 this.intervals
        let k = 0
        let currentStart = tempIntervals[0].start
        let currentEnd = tempIntervals[0].end

        for (let i = 1; i < used; i++) {
          const interval = tempIntervals[i]

          // 检查是否相邻（end + 1 == next.start）
          if (interval.start === currentEnd + 1) {
            // 扩展当前区间
            currentEnd = interval.end
          } else {
            // 写入当前区间（转换为半开区间 [start, end)）
            this.intervals[k++] = currentStart
            this.intervals[k++] = currentEnd + 1 // 闭区间 → 半开区间

            // 开始新区间
            currentStart = interval.start
            currentEnd = interval.end
          }
        }

        // 写入最后一个区间
        this.intervals[k++] = currentStart
        this.intervals[k++] = currentEnd + 1

        // 裁剪到实际长度
        this.intervals.length = k

        // 更新激活的 splat 数量
        this.activeSplats = totalCount

        // PlayCanvas uses row-aligned copy segments for interval remapping, so no
        // per-splat interval remap texture is needed here.
        this.destroyIntervalTexture()
      } else {
        // 完整范围，不需要区间纹理
        this.destroyIntervalTexture()
      }

      // 清空临时数组
      tempIntervals.length = 0
    }
  }

  /**
   * 更新 Transform 状态，检测节点变换是否发生改变
   *
   * @returns 如果世界矩阵发生变化返回 true
   *
   * @remarks
   * PlayCanvas 调用 node.getWorldTransform()
   * Three.js 需要先调用 updateMatrixWorld() 确保最新
   */
  update(): boolean {
    // 确保世界矩阵是最新的
    this.node.updateMatrixWorld(true)

    // 检查矩阵是否改变
    const worldMatrix = this.node.matrixWorld
    const changed = !this.lastMatrix.equals(worldMatrix)

    if (changed) {
      this.lastMatrix.copy(worldMatrix)

      // 更新世界空间 AABB
      this.updateWorldAabb()
    }

    return changed
  }

  /**
   * 更新世界空间 AABB
   * 将本地空间 AABB 通过世界变换矩阵变换到世界空间
   */
  private updateWorldAabb(): void {
    this.worldAabb.copy(this.aabb)
    this.worldAabb.applyMatrix4(this.node.matrixWorld)
  }

  /**
   * 获取世界空间 AABB
   *
   * @param result - 输出的 Box3 对象
   * @returns 是否成功获取
   */
  getWorldAabb(result: THREE.Box3): boolean {
    result.copy(this.worldAabb)
    return true
  }

  /**
   * 重置颜色累积器
   *
   * @param colorUpdateAngle - 触发颜色更新的旋转角度阈值（弧度）
   * @param colorUpdateDistance - 触发颜色更新的平移距离阈值
   *
   * @remarks
   * 使用随机因子初始化累积器，避免所有 splats 同时触发更新
   * 这种随机化策略可以平滑性能开销
   */
  resetColorAccumulators(colorUpdateAngle: number, colorUpdateDistance: number): void {
    // 使用单个随机因子 (0 到 1) 保持旋转和平移累积器同步
    // 这确保旋转和平移阈值以相似的速率触发
    const randomFactor = Math.random()
    this.colorAccumulatedRotation = randomFactor * colorUpdateAngle
    this.colorAccumulatedTranslation = randomFactor * colorUpdateDistance
  }

  /**
   * 检查资源是否包含球谐系数数据
   */
  get hasSphericalHarmonics(): boolean {
    return (this.resource.shBands || 0) > 0
  }

  /**
   * 获取实际要渲染的 splat 数量
   * 等于激活的 splat 数量减去填充数量
   */
  get count(): number {
    return this.activeSplats - this.padding
  }

  /**
   * 将资源内原始 splat 索引映射到 WorkBuffer 中的激活索引。
   *
   * @param originalIndex - 资源内 splat 原始下标
   * @returns 激活索引；不在 active 集合或越界返回 -1
   */
  getActiveSplatIndex(originalIndex: number): number {
    if (originalIndex < 0 || originalIndex >= this.numSplats) {
      return -1
    }

    if (this.intervals.length === 0) {
      return originalIndex < this.activeSplats ? originalIndex : -1
    }

    let activeIndex = 0
    for (let i = 0; i < this.intervals.length; i += 2) {
      const start = this.intervals[i]
      const end = this.intervals[i + 1]

      if (originalIndex < start) {
        break
      }

      if (originalIndex < end) {
        return activeIndex + (originalIndex - start)
      }

      activeIndex += end - start
    }

    return -1
  }

  /**
   * 销毁区间映射纹理
   */
  private destroyIntervalTexture(): void {
    if (this.intervalTexture) {
      this.intervalTexture.dispose()
      this.intervalTexture = null
    }
  }

  /**
   * 获取指定索引 splat 的世界空间中心位置
   * @param index - splat 索引
   * @param target - 可选的目标 Vector3，用于存储结果以避免内存分配
   * @returns 世界空间中心位置
   */
  getSplatWorldCenter(index: number, target?: THREE.Vector3): THREE.Vector3 {
    const result = target || new THREE.Vector3()

    // 优先从 resource.centers 获取（所有资源类型都有这个属性）
    if (this.resource.centers && this.resource.centers.length > index * 3 + 2) {
      const centers = this.resource.centers
      result.set(centers[index * 3], centers[index * 3 + 1], centers[index * 3 + 2])
    } else if (this.resource.gsplatData) {
      // 回退：尝试从 gsplatData 获取
      const gsplatData = this.resource.gsplatData

      // 检查是否是 SogsData（有 getSplatCenter 方法）
      if (isSogsData(gsplatData)) {
        // SogsData (CPU 数据)
        gsplatData.getSplatCenter(index, result)
      } else if (isSplatData(gsplatData)) {
        // SplatData (PLY 格式)
        const x = gsplatData.getProp<Float32Array>('x')
        const y = gsplatData.getProp<Float32Array>('y')
        const z = gsplatData.getProp<Float32Array>('z')
        if (x && y && z) {
          result.set(x[index], y[index], z[index])
        } else {
          throw new Error('[GSplatInfo] SplatData 缺少位置属性（x, y, z）')
        }
      } else {
        // GSplatSogsData (GPU 纹理，无直接 CPU 访问)
        throw new Error(
          '[GSplatInfo] SOGS GPU 资源不支持直接访问 splat 属性（数据在 GPU），且 centers 缓存不可用'
        )
      }
    } else {
      throw new Error('[GSplatInfo] 资源不支持获取 splat 中心位置')
    }

    // 应用世界变换矩阵
    result.applyMatrix4(this.node.matrixWorld)
    return result
  }

  /**
   * 获取指定索引 splat 的世界空间缩放
   * @param index - splat 索引
   * @param target - 可选的目标 Vector3，用于存储结果以避免内存分配
   * @returns 世界空间缩放
   */
  getSplatWorldScale(index: number, target?: THREE.Vector3): THREE.Vector3 {
    const result = target || new THREE.Vector3()

    // 获取本地空间缩放
    if (this.resource.gsplatData) {
      const gsplatData = this.resource.gsplatData

      // 检查是否是 SogsData（有 getSplatScale 方法）
      if (isSogsData(gsplatData)) {
        // SogsData (CPU 数据)
        gsplatData.getSplatScale(index, result)
      } else if (isSplatData(gsplatData)) {
        // SplatData (PLY 格式，log 空间)
        const sx = gsplatData.getProp<Float32Array>('scale_0')
        const sy = gsplatData.getProp<Float32Array>('scale_1')
        const sz = gsplatData.getProp<Float32Array>('scale_2')
        if (sx && sy && sz) {
          result.set(Math.exp(sx[index]), Math.exp(sy[index]), Math.exp(sz[index]))
        } else {
          throw new Error('[GSplatInfo] SplatData 缺少缩放属性（scale_0, scale_1, scale_2）')
        }
      } else {
        // GSplatSogsData (GPU 纹理)
        throw new Error('[GSplatInfo] SOGS GPU 资源不支持直接访问 splat 属性（数据在 GPU）')
      }
    } else {
      throw new Error('[GSplatInfo] 资源不支持获取 splat 缩放')
    }

    // 提取世界矩阵的缩放分量
    const worldScale = new THREE.Vector3()
    this.node.matrixWorld.decompose(new THREE.Vector3(), new THREE.Quaternion(), worldScale)

    // 应用世界缩放
    result.multiply(worldScale)
    return result
  }

  /**
   * 获取指定索引 splat 的世界空间旋转
   * @param index - splat 索引
   * @param target - 可选的目标 Quaternion，用于存储结果以避免内存分配
   * @returns 世界空间旋转四元数
   */
  getSplatWorldRotation(index: number, target?: THREE.Quaternion): THREE.Quaternion {
    const result = target || new THREE.Quaternion()

    // 获取本地空间旋转
    if (this.resource.gsplatData) {
      const gsplatData = this.resource.gsplatData

      // 检查是否是 SogsData（有 getSplatRotation 方法）
      if (isSogsData(gsplatData)) {
        // SogsData (CPU 数据)
        gsplatData.getSplatRotation(index, result)
      } else if (isSplatData(gsplatData)) {
        // SplatData (PLY 格式，四元数: rot_0=w, rot_1=x, rot_2=y, rot_3=z)
        const rx = gsplatData.getProp<Float32Array>('rot_1')
        const ry = gsplatData.getProp<Float32Array>('rot_2')
        const rz = gsplatData.getProp<Float32Array>('rot_3')
        const rw = gsplatData.getProp<Float32Array>('rot_0')
        if (rx && ry && rz && rw) {
          // THREE.Quaternion.set() 格式: (x, y, z, w)
          result.set(rx[index], ry[index], rz[index], rw[index])
          result.normalize()
        } else {
          throw new Error('[GSplatInfo] SplatData 缺少旋转属性（rot_0, rot_1, rot_2, rot_3）')
        }
      } else {
        // GSplatSogsData (GPU 纹理)
        throw new Error('[GSplatInfo] SOGS GPU 资源不支持直接访问 splat 属性（数据在 GPU）')
      }
    } else {
      throw new Error('[GSplatInfo] 资源不支持获取 splat 旋转')
    }

    // 提取世界矩阵的旋转分量
    const worldRotation = new THREE.Quaternion()
    this.node.matrixWorld.decompose(new THREE.Vector3(), worldRotation, new THREE.Vector3())

    // 应用世界旋转：worldRotation * localRotation
    result.premultiply(worldRotation)
    return result
  }

  // ============================================================
  // 状态管理 API（用于中心点渲染模式）
  // ============================================================

  /**
   * 初始化状态数据
   * 在第一次需要状态时调用（懒初始化）
   */
  initState(): void {
    if (this._stateData) return

    this._stateData = new Uint8Array(this.numSplats)
    this._stateDirty = true
  }

  /**
   * 获取状态数据数组（只读）
   */
  get stateData(): Uint8Array | null {
    return this._stateData
  }

  /**
   * 获取状态纹理（用于 GPU 渲染）
   * 如果状态已修改，会自动更新纹理
   */
  get splatStateTexture(): THREE.DataTexture | null {
    if (this._stateDirty) {
      this.updateStateTexture()
    }
    return this._stateTexture
  }

  /**
   * 状态是否需要更新
   */
  get stateDirty(): boolean {
    return this._stateDirty
  }

  /**
   * 设置指定 splat 的状态
   * @param index - splat 索引
   * @param state - 状态值（可以使用 SplatState 枚举的位组合）
   */
  setSplatState(index: number, state: SplatState | number): void {
    if (!this._stateData) {
      this.initState()
    }

    if (index >= 0 && index < this.numSplats) {
      this._stateData![index] = state
      this._stateDirty = true
    }
  }

  /**
   * 获取指定 splat 的状态
   * @param index - splat 索引
   * @returns 状态值，如果状态未初始化则返回 0
   */
  getSplatState(index: number): number {
    if (!this._stateData || index < 0 || index >= this.numSplats) {
      return SplatState.NONE
    }
    return this._stateData[index]
  }

  /**
   * 添加状态标志
   * @param index - splat 索引
   * @param flag - 要添加的状态标志
   */
  addSplatStateFlag(index: number, flag: SplatState): void {
    if (!this._stateData) {
      this.initState()
    }

    if (index >= 0 && index < this.numSplats) {
      this._stateData![index] |= flag
      this._stateDirty = true
    }
  }

  /**
   * 移除状态标志
   * @param index - splat 索引
   * @param flag - 要移除的状态标志
   */
  removeSplatStateFlag(index: number, flag: SplatState): void {
    if (!this._stateData) return

    if (index >= 0 && index < this.numSplats) {
      this._stateData[index] &= ~flag
      this._stateDirty = true
    }
  }

  /**
   * 清除指定 splat 的所有状态
   * @param index - splat 索引
   */
  clearSplatState(index: number): void {
    if (!this._stateData) return

    if (index >= 0 && index < this.numSplats) {
      this._stateData[index] = SplatState.NONE
      this._stateDirty = true
    }
  }

  /**
   * 清除所有 splat 的状态
   */
  clearAllStates(): void {
    if (!this._stateData) return

    this._stateData.fill(SplatState.NONE)
    this._stateDirty = true
  }

  /**
   * 设置选中状态
   * @param indices - 要选中的 splat 索引数组
   * @param selected - 是否选中（true=选中，false=取消选中）
   */
  setSelected(indices: number[], selected: boolean): void {
    if (!this._stateData) {
      this.initState()
    }

    for (const index of indices) {
      if (index >= 0 && index < this.numSplats) {
        if (selected) {
          this._stateData![index] |= SplatState.SELECTED
        } else {
          this._stateData![index] &= ~SplatState.SELECTED
        }
      }
    }
    this._stateDirty = true
  }

  /**
   * 清除所有选中状态
   */
  clearSelection(): void {
    if (!this._stateData) return

    for (let i = 0; i < this.numSplats; i++) {
      this._stateData[i] &= ~SplatState.SELECTED
    }
    this._stateDirty = true
  }

  /**
   * 获取所有选中的 splat 索引
   * @returns 选中的 splat 索引数组
   */
  getSelectedIndices(): number[] {
    if (!this._stateData) return []

    const selected: number[] = []
    for (let i = 0; i < this.numSplats; i++) {
      if (this._stateData[i] & SplatState.SELECTED) {
        selected.push(i)
      }
    }
    return selected
  }

  /**
   * 更新状态纹理
   * 当状态数据修改后，调用此方法将数据上传到 GPU
   */
  updateStateTexture(): void {
    if (!this._stateData || !this._stateDirty) return

    // 计算纹理尺寸（使用正方形纹理）
    const texSize = Math.ceil(Math.sqrt(this.numSplats))

    // 创建或调整纹理大小
    if (!this._stateTexture || this._stateTexture.image.width !== texSize) {
      if (this._stateTexture) {
        this._stateTexture.dispose()
      }

      // 创建填充后的数据（确保纹理为正方形）
      const paddedData = new Uint8Array(texSize * texSize)
      paddedData.set(this._stateData)

      this._stateTexture = new THREE.DataTexture(
        paddedData,
        texSize,
        texSize,
        THREE.RedFormat,
        THREE.UnsignedByteType
      )

      this._stateTexture.name = 'splatState'
      this._stateTexture.minFilter = THREE.NearestFilter
      this._stateTexture.magFilter = THREE.NearestFilter
      this._stateTexture.wrapS = THREE.ClampToEdgeWrapping
      this._stateTexture.wrapT = THREE.ClampToEdgeWrapping
      this._stateTexture.generateMipmaps = false
      this._stateTexture.internalFormat = 'R8'
    } else {
      // 更新现有纹理数据
      const textureData = this._stateTexture.image.data as Uint8Array
      textureData.set(this._stateData)
    }

    this._stateTexture.needsUpdate = true
    this._stateDirty = false
  }
}
